import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { formatearFecha } from '../../utils/fecha';
import BannerFichas from '../../components/BannerFichas/BannerFichas';
import Navbar from '../../components/Navbar/Navbar';
import SessionBar from '../../components/SessionBar/SessionBar';
import Footer from '../../components/Footer/Footer';
import { fetchUserData } from '../../utils/api';
import axiosInstance from '../../services/AxiosInstance';
import { ROUTES } from '../../const/routes';
import BotonChico03 from '../../components/Botones/BotonChico03/BotonChico03';
import { useTranslation } from 'react-i18next';

const BADGE_JUEGO = {
  Roulette: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
  Slots: 'bg-violet-500/20 text-violet-300 border border-violet-500/30',
  BlackJack: 'bg-sky-500/20 text-sky-400 border border-sky-500/30',
};

const DetalleJugada = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);
  const { idJugada } = useParams();
  const [jugada, setJugada] = useState(null);
  const [cargando, setCargando] = useState(true);

  const vieneDesdeMovimientos = location.state?.from === 'movimientos';
  const vieneDesdeHistorialJugador =
    location.state?.from === 'historialJugador';
  const jugadorUserId = location.state?.userId;

  useEffect(() => {
    fetchUserData(navigate, setUserData);
  }, [navigate]);

  const fetchDetalleCompleto = useCallback(async () => {
    if (!idJugada) return;
    setCargando(true);
    try {
      const response = await axiosInstance.get(
        `/api/historial/detalle/${idJugada}`
      );
      const data = response.data;
      data.hora_jugada_local = formatearFecha(data.hora_jugada);
      setJugada(data);
    } catch (error) {
      console.error('Error al obtener detalle de la jugada:', error);
    } finally {
      setCargando(false);
    }
  }, [idJugada]);

  useEffect(() => {
    fetchDetalleCompleto();
  }, [fetchDetalleCompleto]);

  // ─── Subcomponentes de detalle ─────────────────────────────────────────────

  const DetalleRuleta = ({ jugada }) => {
    const { t } = useTranslation();
    if (!jugada.ruleta) return null;
    const { ruleta } = jugada;

    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center gap-2">
          <span className="text-xs text-slate-400 uppercase tracking-widest">
            {t('gameDetail.winningNumber')}
          </span>
          <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-3 inline-flex">
            <img
              src={`${ROUTES.rutaRuletaTodosValoresAlfombra}/valorAlfombra${ruleta.numero_ganador}HoverResized.png`}
              alt={`Resultado ${ruleta.numero_ganador}`}
              style={{ width: '52px', height: '52px' }}
            />
          </div>
        </div>

        {ruleta.colApuestaRuleta && ruleta.colApuestaRuleta.length > 0 && (
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-widest mb-3">
              {t('gameDetail.betsPlaced')}
            </p>
            <div className="space-y-2">
              {ruleta.colApuestaRuleta.map((posicion) => (
                <div
                  key={posicion.id}
                  className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-2.5 md:p-3"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <img
                        src={`${ROUTES.rutaRuletaTodosValoresAlfombra}/valorAlfombra${posicion.eleccion_alfombra}HoverResized.png`}
                        alt={`Resultado ${posicion.eleccion_alfombra}`}
                        className="w-9 h-9 object-contain"
                      />
                      <p className="text-xs font-semibold text-slate-200">
                        {posicion.descripcion}
                      </p>
                    </div>
                    <div className="flex gap-4 text-xs">
                      <div className="flex gap-1">
                        <span className="text-slate-400">{t('gameDetail.betAmount')}</span>
                        <span className="font-semibold text-white">
                          ${posicion.valor_apuesta}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <span className="text-slate-400">{t('gameDetail.prizeAmount')}</span>
                        <span
                          className={`font-semibold ${posicion.valor_recompensa > 0 ? 'text-green-400' : 'text-slate-300'}`}
                        >
                          ${posicion.valor_recompensa}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const DetalleTragamonedas = ({ jugada }) => {
    if (!jugada.tragamonedas) return null;
    const { tragamonedas } = jugada;

    return (
      <div className="space-y-4">
        <div className="flex justify-center">
          <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl px-5 py-3 flex gap-3 flex-wrap justify-center">
            {[1, 2, 3, 4, 5].map((num) => (
              <img
                key={num}
                src={`${ROUTES.rutaTragamonedasValoresTragamonedas}/valor${tragamonedas[`resultado_${num}`]}EstaticoResized.png`}
                alt={`Resultado ${num}`}
                className="h-12 sm:h-16 w-auto object-contain"
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const DetalleBlackJack = ({ jugada }) => {
    const { t } = useTranslation();
    if (!jugada.blackjack) return null;
    const { blackjack } = jugada;
    const manoCrupier = blackjack.rondaBlackjack?.manoCrupier;
    const manosJugador = blackjack.colManoJugador || [];
    const esCrupierBJ =
      manoCrupier?.resultado === 'BlackJack' ||
      (manoCrupier?.colCartaCrupier?.length === 2 &&
        manoCrupier?.valor_cartas === 21);

    return (
      <div className="space-y-3">
        {/* Mano del Crupier */}
        {manoCrupier && (
          <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3">
            <p className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-3">
              {t('gameDetail.dealerHand')}
            </p>
            {(manoCrupier.resultado === 'Busts' || esCrupierBJ) && (
              <div
                className={`text-center mb-3 py-1.5 rounded-lg text-xs font-bold tracking-wide ${
                  esCrupierBJ
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                    : 'bg-red-500/20 text-red-400 border border-red-500/40'
                }`}
              >
                {esCrupierBJ ? t('gameDetail.blackjack') : t('gameDetail.busted')}
              </div>
            )}
            <div className="flex justify-center gap-2 mb-3 flex-wrap">
              {manoCrupier.colCartaCrupier &&
                manoCrupier.colCartaCrupier.map((carta, index) => (
                  <img
                    key={index}
                    className="w-10 h-14 sm:w-12 sm:h-16 drop-shadow-md"
                    src={`${ROUTES.rutaBlackJackCartas}/carta${carta.id}Resized.png`}
                    alt={`${carta.palo_carta}`}
                  />
                ))}
            </div>
            <div className="flex justify-center">
              <span className="bg-slate-700/60 border border-slate-600/60 rounded-lg px-3 py-1 text-xs">
                <span className="text-slate-400">{t('gameDetail.score')}</span>
                <span className="font-bold text-white">
                  {manoCrupier.valor_cartas}
                </span>
              </span>
            </div>
          </div>
        )}

        {/* Manos del Jugador */}
        {manosJugador.map((mano, index) => (
          <div
            key={mano.id}
            className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3"
          >
            <p className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-3">
              {t('gameDetail.handNumber', { number: index + 1 })}
              {jugada.usuario
                ? ` — ${jugada.usuario.nick_usuario}`
                : userData
                  ? ` — ${userData.nick_usuario}`
                  : ''}
            </p>

            {mano.resultado && mano.resultado !== 'Undefined' && (
              <div
                className={`text-center mb-3 py-1.5 rounded-lg text-xs font-bold tracking-wide ${
                  mano.resultado === 'BlackJack' ||
                  mano.resultado === 'BlackJack - Tie'
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                    : mano.resultado === 'Win'
                      ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                      : mano.resultado === 'Tie'
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40'
                        : 'bg-red-500/20 text-red-400 border border-red-500/40'
                }`}
              >
                {mano.resultado.includes('BlackJack')
                  ? t('gameDetail.blackjack')
                  : mano.resultado}
              </div>
            )}

            <div className="flex justify-center gap-2 mb-3 flex-wrap">
              {mano.colCartaJugador &&
                mano.colCartaJugador.map((carta, cartaIndex) => (
                  <img
                    key={cartaIndex}
                    className="w-8 h-12 sm:w-10 sm:h-14 drop-shadow-md"
                    src={`${ROUTES.rutaBlackJackCartas}/carta${carta.id}Resized.png`}
                    alt={`${carta.palo_carta}`}
                  />
                ))}
            </div>

            <div className="flex justify-center mb-3">
              <span className="bg-slate-700/60 border border-slate-600/60 rounded-lg px-3 py-1 text-xs">
                <span className="text-slate-400">{t('gameDetail.score')}</span>
                <span className="font-bold text-white">
                  {mano.valor_cartas}
                </span>
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs border-t border-slate-700/60 pt-2.5">
              <div className="flex gap-1">
                <span className="text-slate-400">{t('gameDetail.betAmount')}</span>
                <span className="font-semibold text-white">
                  ${mano.valor_apuesta}
                </span>
              </div>
              <div className="flex gap-1">
                <span className="text-slate-400">{t('gameDetail.prizeAmount')}</span>
                <span
                  className={`font-semibold ${mano.valor_recompensa > 0 ? 'text-green-400' : 'text-slate-300'}`}
                >
                  ${mano.valor_recompensa}
                </span>
              </div>
            </div>

            {mano.pago_seguro > 0 && (
              <div className="mt-3 pt-2.5 border-t border-slate-700/60">
                <p className="text-xs font-bold mb-2 text-yellow-400 uppercase tracking-widest">
                  {t('gameDetail.insuranceLabel')}
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex gap-1">
                    <span className="text-slate-400">{t('gameDetail.insurancePaid')}</span>
                    <span className="font-semibold text-white">
                      ${mano.pago_seguro}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <span className="text-slate-400">{t('gameDetail.prizeAmount')}</span>
                    <span
                      className={`font-semibold ${mano.recompensa_seguro > 0 ? 'text-green-400' : 'text-red-400'}`}
                    >
                      ${mano.recompensa_seguro}
                    </span>
                  </div>
                  <div className="flex gap-1 col-span-2">
                    <span className="text-slate-400">{t('gameDetail.insuranceResult')}</span>
                    <span
                      className={`font-semibold ${mano.recompensa_seguro > 0 ? 'text-green-400' : 'text-red-400'}`}
                    >
                      {mano.recompensa_seguro > 0
                        ? t('gameDetail.insuranceSuccess')
                        : t('gameDetail.insuranceFailed')}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // ─── Layout base ────────────────────────────────────────────────────────────

  const Layout = ({ children }) => (
    <div className="flex flex-col min-h-screen">
      <BannerFichas />
      <div className="bg-gradient-to-b from-slate-950 to-violet-950 flex-grow">
        <Navbar />
        {userData && <SessionBar />}
        <div className="flex flex-col items-center py-2 md:py-6 px-2 md:px-10">
          {children}
        </div>
      </div>
      <Footer />
    </div>
  );

  if (cargando) {
    return (
      <Layout>
        <h2 className="text-lg md:text-2xl text-yellow-500 font-bold mb-3">
          {t('gameDetail.title')}
        </h2>
        <div className="text-slate-400 animate-pulse text-sm">
          {t('gameDetail.loading')}
        </div>
      </Layout>
    );
  }

  if (!jugada) {
    return (
      <Layout>
        <h2 className="text-lg md:text-2xl text-yellow-500 font-bold mb-3">
          {t('gameDetail.title')}
        </h2>
        <div className="text-slate-400 text-sm">{t('gameDetail.notFound')}</div>
      </Layout>
    );
  }

  const esPropia = jugada.id_usuario === userData?.id;

  return (
    <Layout>
      {!esPropia && jugada.usuario?.nick_usuario && (
        <div className="flex items-center gap-3 my-6">
          {jugada.usuario.avatar && (
            <img
              src={`${ROUTES.rutaAvatares}${jugada.usuario.avatar}`}
              alt={jugada.usuario.nick_usuario}
              className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-yellow-500"
            />
          )}
          <h2 className="text-lg md:text-2xl text-yellow-500 font-bold">
            {t('gameDetail.otherPlayerPrefix')}{jugada.usuario.nick_usuario}
          </h2>
        </div>
      )}
      {/* Tarjeta principal */}
      <div className="w-full max-w-3xl rounded-2xl border-4 border-slate-200 bg-slate-900 shadow-[0_0_28px_rgba(139,92,246,0.35)] overflow-hidden mb-6">
        {/* Cabecera */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/60">
          <span className="text-slate-400 text-[10px] md:text-xs">
            {t('gameDetail.gameIdPrefix')}{jugada.id}
          </span>
          <div className="flex items-center gap-1.5">
            <span
              className={`px-2.5 py-0.5 rounded-full text-[10px] md:text-xs font-semibold ${BADGE_JUEGO[jugada.nombre_juego] ?? 'bg-slate-700 text-slate-300'}`}
            >
              {t(`backend.gameName.${jugada.nombre_juego}`, jugada.nombre_juego)}
            </span>
            {jugada.en_proceso && (
              <span className="px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/30">
                {t('gameDetail.inProgress')}
              </span>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 divide-x divide-slate-700/60 border-b border-slate-700/60 text-center">
          <div className="py-3 px-2">
            <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">
              {t('gameDetail.betLabel')}
            </p>
            <p className="text-sm md:text-base font-bold text-yellow-400">
              ${jugada.total_apostado}
            </p>
          </div>
          <div className="py-3 px-2">
            <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">
              {t('gameDetail.wonLabel')}
            </p>
            <p
              className={`text-sm md:text-base font-bold ${jugada.total_recompensa > 0 ? 'text-green-400' : 'text-red-400'}`}
            >
              ${jugada.total_recompensa}
            </p>
          </div>
          <div className="py-3 px-2">
            <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">
              {t('gameDetail.dateLabel')}
            </p>
            <p className="text-[10px] md:text-xs font-medium text-slate-300">
              {jugada.hora_jugada_local}
            </p>
          </div>
        </div>

        {/* Detalle del juego */}
        <div className="px-3 md:px-5 py-4">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest mb-3">
            {t('gameDetail.detailsLabel')}
          </p>
          {jugada.nombre_juego === 'Roulette' && (
            <DetalleRuleta jugada={jugada} />
          )}
          {jugada.nombre_juego === 'Slots' && (
            <DetalleTragamonedas jugada={jugada} />
          )}
          {jugada.nombre_juego === 'BlackJack' && (
            <DetalleBlackJack jugada={jugada} />
          )}
        </div>
      </div>

      <BotonChico03
        texto={
          vieneDesdeMovimientos
            ? t('gameDetail.backToMovements')
            : t('gameDetail.backToHistory')
        }
        bgColor="pink"
        funcion={() =>
          navigate(
            vieneDesdeMovimientos
              ? ROUTES.movimientos
              : vieneDesdeHistorialJugador
                ? `${ROUTES.historialJugador}/${jugadorUserId}`
                : ROUTES.historial
          )
        }
      />
    </Layout>
  );
};

export default DetalleJugada;
